import { Drawer, Button, Empty, Typography, InputNumber, Spin } from 'antd';
import { DeleteOutlined, ShoppingCartOutlined, CloseOutlined } from '@ant-design/icons';
import { useCartDrawer } from '@contexts';
import { useCart, useAuth, useOrders } from '@hooks';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

const { Title, Paragraph, Text } = Typography;

export const CartDrawer = () => {
  const { drawerOpen, closeDrawer } = useCartDrawer();
  const { profile: { data: profile } } = useAuth();
  const isAuthenticated = !!profile;
  const { cart, isLoading, updateQuantity, removeFromCart, clearCart } = useCart(isAuthenticated);
  const { createCheckoutSession } = useOrders();
  const navigate = useNavigate();
  const [checkingOut, setCheckingOut] = useState(false);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  const getProductImage = (imageUrl: string, productName: string) => {
    if (imageUrl && imageUrl.startsWith('http')) {
      return imageUrl;
    } else if (imageUrl) {
      const bucketName = import.meta.env.VITE_AWS_S3_BUCKET || 'estabrookinc-production';
      const region = import.meta.env.VITE_AWS_S3_BUCKET_REGION || 'us-west-2';
      return `https://${bucketName}.s3.${region}.amazonaws.com/${imageUrl}`;
    }
    return `https://placehold.co/100x100/1a1a1a/00ade6?text=${encodeURIComponent(productName)}`;
  };

  const handleQuantityChange = async (cartItemId: string, newQuantity: number) => {
    try {
      await updateQuantity(cartItemId, newQuantity);
    } catch (error) {
      console.error('Failed to update quantity:', error);
    }
  };

  const handleRemoveItem = async (cartItemId: string) => {
    try {
      await removeFromCart(cartItemId);
    } catch (error) {
      console.error('Failed to remove item:', error);
    }
  };

  const handleClearCart = async () => {
    try {
      await clearCart();
    } catch (error) {
      console.error('Failed to clear cart:', error);
    }
  };

  const handleCheckout = async () => {
    if (!isAuthenticated) {
      closeDrawer();
      navigate('/login');
      return;
    }

    if (!cart || cart.items.length === 0) {
      return;
    }

    setCheckingOut(true);
    try {
      // Include variantId to ensure correct product variant is processed
      await createCheckoutSession.mutateAsync({
        lineItems: cart.items.map(item => ({
          productId: item.productId,
          variantId: item.variantId,
          quantity: item.quantity,
        })),
        successUrl: `${window.location.origin}/order-success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/merch`,
      });

      // Clear cart after successful checkout session creation
      await clearCart();
      closeDrawer();
    } catch (error) {
      console.error('Checkout error:', error);
      setCheckingOut(false);
    }
  };

  return (
    <Drawer
      title={
        <div className="flex items-center justify-center w-full pl-8">
          <Title level={3} className="font-azonix !text-[#00ade6] !mb-0 uppercase !font-normal !mt-1">
            Shopping Cart
          </Title>
        </div>
      }
      placement="right"
      open={drawerOpen}
      onClose={closeDrawer}
      width={450}
      closeIcon={<CloseOutlined className="text-[#00ade6] text-xl" style={{ color: '#00ade6' }} />}
      className="cart-drawer"
      styles={{
        header: {
          backgroundColor: '#1a1a1a',
          borderBottom: '2px solid #00ade6',
          padding: '20px 24px',
        },
        body: {
          backgroundColor: '#000000',
          padding: 0,
        },
      }}
    >
      {isLoading ? (
        <div className="flex items-center justify-center h-full">
          <Spin size="large" />
        </div>
      ) : !cart || cart.items.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-full px-6">
          <Empty
            image={<ShoppingCartOutlined style={{ fontSize: 80, color: '#00ade6' }} />}
            description={
              <Paragraph className="font-orbitron text-zinc-400 text-lg font-normal mt-4">
                Your cart is empty
              </Paragraph>
            }
          />
          <Button
            type="primary"
            size="large"
            onClick={() => {
              closeDrawer();
              navigate('/merch');
            }}
            className="btn-single-cut bg-[#00ade6] border-none font-azonix font-normal text-white text-lg mt-6"
          >
            Continue Shopping
          </Button>
        </div>
      ) : (
        <div className="flex flex-col h-full">
          {/* Cart Items List */}
          <div className="flex-1 overflow-y-auto p-6">
            {cart.items.map((item) => (
              <div
                key={item.id}
                className="bg-zinc-900 border border-zinc-700 p-4 mb-4 hover:border-[#00ade6]/50 transition-colors"
              >
                <div className="flex gap-4">
                  {/* Product Image */}
                  <div className="w-20 h-20 flex-shrink-0 bg-zinc-800 overflow-hidden border border-zinc-700">
                    <img
                      src={getProductImage(item.productImage, item.productName)}
                      alt={item.productName}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Product Details */}
                  <div className="flex-1 min-w-0">
                    <Paragraph className="font-coda text-white text-base font-normal !mb-1 truncate uppercase">
                      {item.productName}
                    </Paragraph>
                    {(item.size || item.color) && (
                      <div className="flex flex-col gap-1 mb-2">
                        {item.size && (
                          <Text className="font-coda text-white text-sm font-normal">
                            Size: <span className="text-primary uppercase">{item.size}</span>
                          </Text>
                        )}
                        {item.color && (
                          <Text className="font-coda text-white text-sm font-normal">
                            Color: <span className="text-primary uppercase">{item.color}</span>
                          </Text>
                        )}
                      </div>
                    )}
                    <Text className="font-coda text-white text-lg font-normal">
                      {formatPrice(item.productPrice)}
                    </Text>

                    {/* Quantity Control */}
                    <div className="flex items-center gap-3 mt-3">
                      <InputNumber
                        min={1}
                        max={99}
                        value={item.quantity}
                        onChange={(value) => handleQuantityChange(item.id, value || 1)}
                        className="w-20"
                        size="small"
                      />
                      <Text className="font-coda text-white text-sm font-normal">
                        Subtotal: <span className="text-[#00ade6]">{formatPrice(item.subtotal)}</span>
                      </Text>
                    </div>
                  </div>

                  {/* Remove Button */}
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemoveItem(item.id)}
                    className="cart-item-delete-btn !text-red-500"
                  />
                </div>
              </div>
            ))}

            {/* Clear Cart Button */}
            {cart.items.length > 0 && (
              <Button
                type="text"
                onClick={handleClearCart}
                className="clear-cart-button-3d font-azonix font-normal uppercase w-full mt-2 text-red-500"
              >
                Clear Cart
              </Button>
            )}
          </div>

          {/* Cart Summary Footer */}
          <div className="border-t-2 border-[#00ade6] bg-zinc-900 p-6">
            {/* Total Items */}
            <div className="flex justify-between items-center mb-3">
              <Text className="font-azonix text-white text-base font-normal">
                Total Items:
              </Text>
              <Text className="font-azonix text-[#00ade6] text-base font-normal">
                {cart.totalItems}
              </Text>
            </div>

            {/* Total Amount */}
            <div className="flex justify-between items-center mb-6 pb-4 border-b border-zinc-700">
              <Text className="font-azonix text-white text-xl font-normal uppercase">
                Total:
              </Text>
              <Text className="font-azonix text-[#00ade6] text-2xl font-normal">
                {formatPrice(cart.totalAmount)}
              </Text>
            </div>

            {/* Checkout Button */}
            <Button
              type="primary"
              size="large"
              loading={checkingOut}
              onClick={handleCheckout}
              className="btn-single-cut bg-[#00ade6] border-none font-azonix font-normal text-white text-xl w-full h-14 uppercase"
            >
              {isAuthenticated ? 'Proceed to Checkout' : 'Login to Checkout'}
            </Button>

            <Button
              type="primary"
              size="large"
              onClick={() => {
                closeDrawer();
                navigate('/merch');
              }}
              className="btn-single-cut bg-[#00ade6] border-none font-azonix font-normal text-white text-base w-full mt-3 uppercase"
            >
              Continue Shopping
            </Button>
          </div>
        </div>
      )}
    </Drawer>
  );
};

